import { t } from '@whitesharx/starfall-frontend-ttranslate';
import SayKitWeb from '@whitesharx/starfall-frontend-saykit-web';
import type { Key, GameConfig } from '@whitesharx/starfall-frontend-chessground';
import {
  GameMode,
  GameResultTitle,
  COMPLETED_ONBOARDING_GAMES_COUNT_KEY,
  ONBOARDING_GAME_KEY,
  PLAYER_COLOR,
  ONBOARDING_GAMES_COUNT,
  TIMERS_BY_MODE,
  resultGetterMap,
} from '../constants';
import {
  CalculatePlayersTurnTimesParams,
  GameOverReason,
  GameResult,
  GetFloatTimesParams,
  GetTimersParams,
  MoveAPI,
  OnboardingGameState,
  PersistedOnboardingGame,
  PhrasePackName,
  PhraseType,
  PlayerColor,
  PlayerState,
  Result,
  Sticker,
} from '../types';
import { getBotThinkingTimeMS, random } from './botThinkingTime';

const DEFAULT_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export const persistGame = (state: PersistedOnboardingGame) => {
  localStorage.setItem(ONBOARDING_GAME_KEY, JSON.stringify(state));
};

export const removePersistedGame = () => {
  localStorage.removeItem(ONBOARDING_GAME_KEY);
};

export const getGameNumber = () => Number(localStorage.getItem(COMPLETED_ONBOARDING_GAMES_COUNT_KEY)) + 1;

export const getLeftTime = (expiredAt: number) => {
  const leftTime = expiredAt - Date.now();
  return leftTime > 0 ? leftTime : 0;
};

export const calculateRemainingTime = (startDate: number, turnTimeMS?: number | null) => {
  if (startDate && turnTimeMS) {
    return getLeftTime(startDate + turnTimeMS);
  }
};

// todo: refactor change player & enemy to white and black
const calculatePlayersTurnTimes = (params: CalculatePlayersTurnTimesParams) => {
  const playerStartDate = params.isPlayerMove ? new Date(params.startDate) : new Date();
  const enemyStartDate = params.isPlayerMove ? new Date() : new Date(params.startDate);
  const playerTurnTime = calculateRemainingTime(playerStartDate.getTime(), params.playerTime);
  const enemyTurnTime = calculateRemainingTime(enemyStartDate.getTime(), params.enemyTime);

  return { playerTurnTime, enemyTurnTime };
};

export const getTimers = (params: GetTimersParams) => {
  const lastMove = params.moves[params.moves.length - 1];
  const isWhitePlayer = params.playerColor === PLAYER_COLOR.white;
  const isPlayerMove = lastMove ? lastMove.playerId !== params.playerId : isWhitePlayer;
  const playerTime = isWhitePlayer ? params.whiteLeftTime : params.blackLeftTime;
  const enemyTime = isWhitePlayer ? params.blackLeftTime : params.whiteLeftTime;

  const { playerTurnTime, enemyTurnTime } = calculatePlayersTurnTimes({
    startDate: lastMove?.date || params.createdAt!,
    playerTime,
    enemyTime,
    isPlayerMove,
  });

  return { player: playerTurnTime, enemy: enemyTurnTime };
};

export const getFloatLeftTime = (calcTime: boolean, lastMoveTime: number, leftTime?: number) => {
  if (leftTime && calcTime) {
    return leftTime - lastMoveTime;
  }

  return leftTime;
};

export const getFloatTimes = (params: GetFloatTimesParams) => {
  const isWhiteMove = params.turn === PLAYER_COLOR.white;
  const whiteLeftTime = getFloatLeftTime(!isWhiteMove, params.lastMoveTime, params.whiteLeftTime) || 0;
  const blackLeftTime = getFloatLeftTime(isWhiteMove, params.lastMoveTime, params.blackLeftTime) || 0;
  return {
    whiteLeftTime: Math.max(0, whiteLeftTime),
    blackLeftTime: Math.max(0, blackLeftTime),
  };
};

const PLAYER_ID = 'player';

export const getInitialGameState = (savedGame?: PersistedOnboardingGame): OnboardingGameState => {
  const createdAt = savedGame?.createdAt || new Date().getTime();
  const mode =
    savedGame?.mode || (getGameNumber() < ONBOARDING_GAMES_COUNT ? GameMode.GAMEWITHOUTTIMERS : GameMode.RAPID30);

  const moves = savedGame?.moves || [];
  const lastMove = moves[moves.length - 1];
  const isPlayerMove = lastMove ? lastMove.playerId !== PLAYER_ID : true;

  const times = savedGame
    ? {
        whiteLeftTime: savedGame.whiteLeftTime,
        blackLeftTime: savedGame.blackLeftTime,
      }
    : TIMERS_BY_MODE[mode];

  const timers =
    mode === GameMode.RAPID30
      ? getTimers({
          ...times,
          moves,
          playerColor: PLAYER_COLOR.white,
          playerId: PLAYER_ID,
          createdAt,
        })
      : undefined;

  const turnTime = isPlayerMove ? timers?.player : timers?.enemy;
  const isGameOver = !!savedGame?.isGameOver || (mode === GameMode.RAPID30 && !turnTime);

  return {
    createdAt,
    enemy: {
      ...savedGame?.enemy,
      turnTime: timers?.enemy,
      pause: isGameOver || isPlayerMove,
    },
    mode,
    whitePlayerId: PLAYER_ID,
    player: {
      name: 'You',
      id: PLAYER_ID,
      online: true,
      color: PLAYER_COLOR.white,
      turnTime: timers?.player,
      pause: isGameOver || !isPlayerMove,
    },
    turn: savedGame?.turn || PLAYER_COLOR.white,
    ...(mode === GameMode.RAPID30 && times),
    isGameOver,
    fen: savedGame?.fen || DEFAULT_FEN,
  };
};

export const getInitialConfig = (persisted?: PersistedOnboardingGame): Omit<GameConfig, 'events'> => {
  const mode =
    persisted?.mode || (getGameNumber() < ONBOARDING_GAMES_COUNT ? GameMode.GAMEWITHOUTTIMERS : GameMode.RAPID30);

  const moves = persisted?.moves || [];
  const lastMove = moves[moves.length - 1];

  return {
    withBot: true,
    botMode: getGameNumber() < ONBOARDING_GAMES_COUNT ? 'adaptive.fastwin.5' : 'adaptive.fastwin.10',
    botThinkingTime: getBotThinkingTimeMS({ mode, movesCount: moves.length }),
    pauseBot: !!persisted?.isGameOver,
    playerColor: 'white',
    fen: persisted?.fen || DEFAULT_FEN,
    lastMove: persisted?.isGameOver || !lastMove ? undefined : { orig: lastMove.from as Key, dest: lastMove.to as Key },
    viewOnly: !!persisted?.isGameOver,
  };
};

export const restoreGameConfig = () => {
  const persisted = getPersistedGame();

  return getInitialConfig(persisted);
};

export const isNeedRecalculateTimers = (isGameOver: boolean, hasTurnTime: boolean) => !isGameOver || !hasTurnTime;

export const getPersistedGame = () => {
  const persisted = localStorage.getItem(ONBOARDING_GAME_KEY);

  return persisted ? (JSON.parse(persisted) as PersistedOnboardingGame) : undefined;
};

export const restoreGameState = () => {
  const persisted = getPersistedGame();

  return getInitialGameState(persisted);
};

export const getStartGameData = (mode: GameMode) => ({
  type: mode,
  mode: 'Bot',
  gameNumber: getGameNumber(),
  time: millisecondsToSeconds(performance.now() - SayKitWeb.getBackgroundTime()),
});

export const getEndGameData = (game: OnboardingGameState, result: GameResult) => ({
  type: game.mode,
  mode: 'Bot',
  resultType: result ? getResultType(result) : null,
  gameNumber: getGameNumber(),
  time: millisecondsToSeconds(performance.now() - SayKitWeb.getBackgroundTime()),
});

enum AnaliticsResultType {
  drawAny = 'DrawAny',
  winTimeExpired = 'WinTimeExpired',
  winCheckmated = 'WinCheckMated',
  winSurrendered = 'WinSurrendered',
  loseTimeExpired = 'LoseTimeExpired',
  loseCheckmated = 'LoseCheckMated',
  loseSurrendered = 'LoseSurrendered',
  didNotTakePlace = 'DidNotTakePlace',
  unexpected = 'Unexpected',
}

const getResultType = ({ title, reason }: GameResult): AnaliticsResultType => {
  if ([GameResultTitle.draw, GameResultTitle.stalemate].includes(title)) {
    return AnaliticsResultType.drawAny;
  }

  if (title === GameResultTitle.victory) {
    if (reason === 'SURRENDERED') {
      return AnaliticsResultType.winSurrendered;
    }

    return AnaliticsResultType.winCheckmated;
  }

  if (title === GameResultTitle.defeat) {
    if (reason === 'SURRENDERED') {
      return AnaliticsResultType.loseSurrendered;
    }

    return AnaliticsResultType.loseCheckmated;
  }

  if (title === GameResultTitle.canceled) {
    return AnaliticsResultType.didNotTakePlace;
  }

  return AnaliticsResultType.unexpected;
};

const BOT_COUNTRIES = ['US', 'GB', 'IN', 'IQ', 'FR', 'FI', 'AU', 'NO', 'KR', 'NE', 'JP', 'CN', 'DE', 'EE', 'CA', 'AR'];

export const getEnemyData = () => ({
  online: true,
  id: 'onboarding_bot',
  name: 'Player' + random(100, 999),
  color: PLAYER_COLOR.black,
  country: BOT_COUNTRIES[random(0, BOT_COUNTRIES.length - 1)],
});

type GetGameResultParams = {
  reason: GameOverReason;
  turn: PlayerColor;
  enemyId: string;
  playerId: string;
};

const getWinnerId = (params: GetGameResultParams) => {
  if (params.reason === 'SURRENDERED') {
    return params.enemyId;
  }

  const playedIdByColor: Record<PlayerColor, string> = {
    w: params.playerId,
    b: params.enemyId,
  };

  const nextTurn = params.turn === PLAYER_COLOR.white ? PLAYER_COLOR.black : PLAYER_COLOR.white;

  return params.reason === 'TURN_TIME' ? playedIdByColor[nextTurn] : playedIdByColor[params.turn];
};

export const getGameResult = (params: GetGameResultParams) => {
  const winnerId = getWinnerId(params);

  return {
    whitePlayerId: params.playerId,
    winner: {
      playerId: winnerId,
      reason: params.reason,
    },
  };
};

export const getGameOverReasonByMoves = (moves: MoveAPI[]) => (moves.length > 1 ? 'TURN_TIME' : 'ABORTED');

export const formatGameResult = (result: Result, player: PlayerState, enemy: PlayerState) => {
  const reason = result.winner!.reason;
  const isPlayerWinner = player.id === result.winner!.playerId;
  const { title, description } = resultGetterMap[reason](isPlayerWinner);

  return { title, description, enemy, player, reason };
};

export const getProductName = (id: string): string => {
  const valueIdArray = id.split('com.whitesharx.starfall.iap.');
  return valueIdArray[valueIdArray.length - 1];
};

export const getPhraseText = (phrase: PhraseType) => {
  const packName = getProductName(phrase.packId) as PhrasePackName;
  return t(`phrases.${packName}.item${phrase.id}`);
};

export const getPackPhrases = (packId: string) => {
  const PHRASES_COUNT_IN_PACK = 6;
  return Array.from({ length: PHRASES_COUNT_IN_PACK }).map((_, i) => ({ packId, id: i + 1 })) as PhraseType[];
};

export const getPackEmoticons = (packId: string) => {
  const EMOTICONS_COUNT_IN_PACK = 5;
  return Array.from({ length: EMOTICONS_COUNT_IN_PACK }).map((_, i) => ({ packId, id: i + 1 })) as Sticker[];
};

export const getStickerPackSrc = (sticker: Sticker) => {
  const packName = getProductName(sticker.packId);
  return `assets/images/shop/emoticons/${packName}/emoji.set${packName.match(/\d+/g)?.[0]}.${sticker.id}.webp`;
};

const millisecondsToSeconds = (time: number) => {
  return Math.trunc(time / 1000);
};

export const toMovesPairs = <T extends MoveAPI>(moves: T[]): { white: T; black?: T; number: number }[] => {
  const pairs = [];

  for (let index = 0, number = 1; index <= moves.length - 1; index += 2, number++) {
    const white = moves[index];
    const black = moves[index + 1];

    pairs.push({ white, black, number });
  }

  return pairs;
};
